import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { api, errMsg, useFetch } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { money, fmtDate, STATUS_LABELS, PAYMENT_LABELS, PROVINCES } from "../lib/shop";
import { Seo } from "../components/site/Seo";
import { Timeline } from "./TrackOrder";
import { OrderSummary } from "./OrderConfirmation";

export function AuthForm({ admin = false }) {
  const { setUser } = useStore();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ email: "", password: "", first_name: "", last_name: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = mode === "login" ? await api.post("/auth/login", { email: f.email, password: f.password, admin }) : await api.post("/auth/register", f);
      setUser(r.data);
      if (admin) nav("/admin");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="card-dark p-6 sm:p-8 space-y-4 max-w-md w-full" data-testid={admin ? "admin-login-form" : "account-auth-form"}>
      {mode === "register" && (
        <div className="grid grid-cols-2 gap-3">
          <div><label className="label">First name</label><input required className="field" value={f.first_name} onChange={set("first_name")} data-testid="register-first-name-input" /></div>
          <div><label className="label">Last name</label><input required className="field" value={f.last_name} onChange={set("last_name")} data-testid="register-last-name-input" /></div>
          <div className="col-span-2"><label className="label">Mobile (optional)</label><input className="field" value={f.phone} onChange={set("phone")} data-testid="register-phone-input" /></div>
        </div>
      )}
      <div><label className="label">Email</label><input required type="email" autoComplete="email" className="field" value={f.email} onChange={set("email")} data-testid="login-email-input" /></div>
      <div><label className="label">Password</label><input required type="password" minLength={mode === "register" ? 8 : 1} autoComplete={mode === "login" ? "current-password" : "new-password"} className="field" value={f.password} onChange={set("password")} data-testid="login-password-input" /></div>
      <button disabled={busy} className="btn-gold w-full" data-testid={admin ? "admin-login-button" : "login-submit-button"}>{busy ? "Please wait..." : mode === "login" ? "Log in" : "Create account"}</button>
      {!admin && (
        <button type="button" onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-sm text-zinc-400 w-full" data-testid="login-register-link">
          {mode === "login" ? "New here? Create an account" : "Already have an account? Log in"}
        </button>
      )}
    </form>
  );
}

function Orders() {
  const { data } = useFetch("/account/orders");
  const [open, setOpen] = useState(null);
  if (!data) return <p className="text-zinc-400">Loading...</p>;
  if (!data.length) return <p className="text-zinc-400" data-testid="account-no-orders">No orders yet.</p>;
  return (
    <div className="space-y-3" data-testid="account-orders">
      {data.map((o) => (
        <div key={o.id} className="card-dark p-5">
          <button className="w-full flex flex-wrap justify-between gap-2 text-left" onClick={() => setOpen(open === o.id ? null : o.id)} data-testid={`account-order-${o.order_number}`}>
            <span><b className="font-display text-2xl text-gold">{o.order_number}</b><span className="block text-xs text-zinc-400">{fmtDate(o.created_at)} · {o.items.length} item(s)</span></span>
            <span className="text-right text-sm"><b>{money(o.total)}</b><span className="block text-xs text-zinc-400">{STATUS_LABELS[o.status]} · {PAYMENT_LABELS[o.payment_status]}</span></span>
          </button>
          {open === o.id && <div className="grid lg:grid-cols-2 gap-6 mt-6"><Timeline order={o} /><OrderSummary o={o} /></div>}
        </div>
      ))}
    </div>
  );
}

function Profile() {
  const { user, setUser } = useStore();
  const [f, setF] = useState({ first_name: user.first_name, last_name: user.last_name, phone: user.phone || "", addresses: user.addresses || [] });
  const [addr, setAddr] = useState({ label: "Home", street: "", suburb: "", city: "", province: "", postal_code: "" });
  const save = async (next) => {
    try { const r = await api.put("/account/profile", next); setUser(r.data); setF({ ...next, addresses: r.data.addresses }); toast.success("Saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const a = (k) => (e) => setAddr({ ...addr, [k]: e.target.value });
  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <form className="card-dark p-6 space-y-4" onSubmit={(e) => { e.preventDefault(); save(f); }} data-testid="account-profile-form">
        <h3 className="font-display text-3xl">Profile</h3>
        <p className="text-sm text-zinc-400">{user.email}</p>
        {["first_name", "last_name", "phone"].map((k) => (
          <div key={k}><label className="label">{k.replace("_", " ")}</label><input className="field" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`profile-${k.replace("_", "-")}-input`} /></div>
        ))}
        <button className="btn-gold" data-testid="profile-save-button">Save profile</button>
      </form>
      <div className="card-dark p-6 space-y-4" data-testid="account-addresses">
        <h3 className="font-display text-3xl">Saved addresses</h3>
        {f.addresses.map((x) => (
          <div key={x.id} className="flex justify-between gap-3 text-sm border-b border-white/10 pb-3">
            <span><b>{x.label}</b><br />{x.street}, {x.suburb} {x.city}, {x.province} {x.postal_code}</span>
            <button className="text-red-400 text-xs" onClick={() => save({ ...f, addresses: f.addresses.filter((y) => y.id !== x.id) })} data-testid={`address-remove-${x.id}`}>Remove</button>
          </div>
        ))}
        <form className="grid grid-cols-2 gap-3" onSubmit={(e) => { e.preventDefault(); save({ ...f, addresses: [...f.addresses, addr] }); setAddr({ ...addr, street: "", suburb: "", city: "", postal_code: "" }); }}>
          <input className="field" placeholder="Label" value={addr.label} onChange={a("label")} data-testid="address-label-input" />
          <input className="field" required placeholder="Street" value={addr.street} onChange={a("street")} data-testid="address-street-input" />
          <input className="field" placeholder="Suburb" value={addr.suburb} onChange={a("suburb")} data-testid="address-suburb-input" />
          <input className="field" required placeholder="City" value={addr.city} onChange={a("city")} data-testid="address-city-input" />
          <select className="field" required value={addr.province} onChange={a("province")} data-testid="address-province-select"><option value="">Province</option>{PROVINCES.map((p) => <option key={p}>{p}</option>)}</select>
          <input className="field" required pattern="\d{4}" placeholder="Postal code" value={addr.postal_code} onChange={a("postal_code")} data-testid="address-postal-input" />
          <button className="btn-outline col-span-2" data-testid="address-add-button">Add address</button>
        </form>
      </div>
    </div>
  );
}

export default function Account() {
  const { user, setUser } = useStore();
  const logout = async () => { await api.post("/auth/logout"); setUser(false); };
  if (user === null) return <div className="container-x py-24 text-zinc-400">Loading...</div>;
  return (
    <div className="container-x py-12 sm:py-20">
      <Seo title="My Account" />
      <p className="eyebrow">Optional account</p>
      <h1 className="font-display text-6xl sm:text-8xl leading-none mt-2">{user ? `Hi, ${user.first_name}` : "My Account"}</h1>
      {!user ? (
        <div className="mt-8 grid lg:grid-cols-2 gap-10 items-start">
          <AuthForm />
          <p className="text-zinc-400 text-sm max-w-sm">An account is optional — you can always check out as a guest. Accounts let you save delivery addresses and see your order history.</p>
        </div>
      ) : (
        <Tabs defaultValue="orders" className="mt-8">
          <div className="flex justify-between items-center gap-4 flex-wrap">
            <TabsList className="bg-[#121215]">
              <TabsTrigger value="orders" data-testid="account-tab-orders">Orders</TabsTrigger>
              <TabsTrigger value="profile" data-testid="account-tab-profile">Profile & addresses</TabsTrigger>
            </TabsList>
            {user.role === "admin" && <a href="/admin" className="text-gold text-sm underline">Admin dashboard</a>}
            <button onClick={logout} className="text-sm text-zinc-400 hover:text-white" data-testid="account-logout-button">Log out</button>
          </div>
          <TabsContent value="orders" className="mt-6"><Orders /></TabsContent>
          <TabsContent value="profile" className="mt-6"><Profile /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}
